import React, { useState, useRef } from 'react';
import { X, Package } from 'lucide-react';
import { VentaActividad } from '../lib/supabase';
import { ventaActividadService } from '../services/ventaActividadService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { fechaEnChile } from '../utils/dateHelpers';

interface RegistrarEntregaPedidoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pedido: VentaActividad | null;
  onSuccess: () => void;
  /** 'nested' cuando se abre sobre otra ventana ya abierta (ej. el detalle de pedido). */
  level?: 'base' | 'nested';
}

export const RegistrarEntregaPedidoModal: React.FC<RegistrarEntregaPedidoModalProps> = ({
  isOpen,
  onClose,
  pedido,
  onSuccess,
  level = 'base'
}) => {
  const [loading, setLoading] = useState(false);
  const [cantidad, setCantidad] = useState<number>(0);
  const [fechaEntrega, setFechaEntrega] = useState(() => fechaEnChile(new Date()) ?? '');
  const fechaEntregaInicialRef = useRef(fechaEntrega);
  const [notas, setNotas] = useState('');

  // Inicializar cantidad al máximo pendiente cuando se abre el modal
  React.useEffect(() => {
    if (isOpen && pedido) {
      const pendiente = pedido.cantidad - pedido.cantidad_entregada;
      setCantidad(pendiente);
      const fechaInicial = fechaEnChile(new Date()) ?? '';
      fechaEntregaInicialRef.current = fechaInicial;
      setFechaEntrega(fechaInicial);
      setNotas('');
    }
  }, [isOpen, pedido]);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  // Función para detectar si hay cambios sin guardar
  const hasFormChanges = () => {
    if (!pedido) return false;
    const pendiente = pedido.cantidad - pedido.cantidad_entregada;
    return cantidad !== pendiente || notas.trim() !== '' || fechaEntrega !== fechaEntregaInicialRef.current;
  };

  const hasUnsavedChanges = loading || hasFormChanges();
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pedido) return;

    if (cantidad <= 0) {
      handleError('La cantidad debe ser mayor a 0');
      return;
    }

    const cantidadPendiente = pedido.cantidad - pedido.cantidad_entregada;
    if (cantidad > cantidadPendiente) {
      handleError('La cantidad no puede ser mayor a la cantidad pendiente');
      return;
    }

    handleError('');
    setLoading(true);

    try {
      await ventaActividadService.registrarEntrega(pedido.id, {
        cantidad,
        fecha: fechaEntrega,
        notas: notas || undefined
      });

      onSuccess();
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos registrar la entrega.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !pedido) return null;

  const cantidadPendiente = pedido.cantidad - pedido.cantidad_entregada;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      level={level}
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Registrar Entrega</h2>
              <p className="text-blue-100 text-sm">Pedido #{pedido.id.slice(0, 8)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Cantidad Total:</span>
              <span className="font-semibold text-slate-900">
                {pedido.cantidad} {pedido.cantidad === 1 ? 'unidad' : 'unidades'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Cantidad Entregada:</span>
              <span className="font-semibold text-blue-600">
                {pedido.cantidad_entregada} {pedido.cantidad_entregada === 1 ? 'unidad' : 'unidades'}
              </span>
            </div>
            <div className="border-t border-slate-200 pt-2 mt-2"></div>
            <div className="flex justify-between">
              <span className="text-slate-700 font-semibold">Cantidad Pendiente:</span>
              <span className="font-bold text-lg text-orange-600">
                {cantidadPendiente} {cantidadPendiente === 1 ? 'unidad' : 'unidades'}
              </span>
            </div>
          </div>

          <FormErrorAlert error={error} onClear={clearError} />

          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <TextInput
                label="Cantidad a Entregar"
                type="number"
                value={String(cantidad || '')}
                onChange={(v) => setCantidad(Number(v))}
                min={1}
                max={cantidadPendiente}
                placeholder="0"
                required
              />
            </div>
            <div className="w-40 flex-shrink-0">
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                Fecha de Entrega
              </label>
              <input
                type="date"
                value={fechaEntrega}
                onChange={(e) => setFechaEntrega(e.target.value)}
                className={`w-full px-3 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
              />
            </div>
          </div>
          <div>
            {
              <div className="mt-2">
                <span className="text-xs text-slate-500">Elegir por unidades:</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Array.from({ length: cantidadPendiente + 1 }, (_, i) => i).map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => setCantidad(qty)}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        cantidad === qty
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-700'
                      }`}
                    >
                      {qty}
                    </button>
                  ))}
                </div>
              </div>
            }
          </div>

          <Textarea
            label="Notas"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={3}
            placeholder="Información adicional sobre esta entrega..."
          />

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Registrando...' : 'Registrar Entrega'}
            </button>
          </div>
        </form>
    </DialogShell>
  );
};
